import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { translate } from '@fondationcss/sdk';
import { getCustomSdk } from '@/sdk';
import type { SpecialistGroup } from '@/sdk/dto/specialist';
import { loadFailureKind, serverMessage, type LoadFailureKind } from '@/utils/api-error';
import { pickSpecialistGroup } from '@/views/specialist/specialist-groups';

const STORAGE_KEY = 'specialist-group-selection';

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(id: string | null) {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // A browser that refuses its storage keeps the choice for the session only.
  }
}

/**
 * The groups of the specialist's students (#1445), and the one their two group pages show. The
 * context bar offers the list — its « Groupe » menu on those routes — and the pages read the choice:
 * it follows from one page to the other as the teacher's bar does, and the browser remembers it
 * from one visit to the next.
 */
export const useSpecialistGroupStore = defineStore('specialistGroup', () => {
  const groups = ref<SpecialistGroup[]>([]);
  /** Null until read; false when the reader holds the permission but no specialist record. */
  const isSpecialist = ref<boolean | null>(null);
  const loading = ref(false);
  const loadError = ref<string | null>(null);
  /** The server refused the list: the permission was withdrawn since sign-in. */
  const refused = ref(false);
  const selectedId = ref<string | null>(null);
  /** The group on screen: one of the list, never an id it no longer holds. */
  const selected = computed(() => groups.value.find(group => group.id === selectedId.value) ?? null);

  function choose(id: string | null) {
    selectedId.value = id;
    writeStored(id);
  }

  /**
   * Reads the list again. The group shown is then `preferred` — the one the address names — if it
   * is still one of theirs, else the one on screen, else the one remembered, else the first.
   * Resolves to why the list could not be read, null once read.
   */
  async function load(preferred?: string | null): Promise<LoadFailureKind | null> {
    loading.value = true;
    loadError.value = null;
    try {
      const response = await getCustomSdk().getMySpecialistGroups({ errorHandler: null });
      groups.value = response.groups;
      isSpecialist.value = response.isSpecialist;
      refused.value = false;
      choose(pickSpecialistGroup(response.groups, preferred, selectedId.value, readStored()));
      return null;
    } catch (e) {
      const kind = loadFailureKind(e);
      if (kind === 'refused') refused.value = true;
      else loadError.value = serverMessage(e, translate('parcours.specialist.groups.loadError'));
      return kind;
    } finally {
      loading.value = false;
    }
  }

  return { groups, isSpecialist, loading, loadError, refused, selectedId, selected, choose, load };
});
